/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: Dashboard
 * (C) Copyright IBM Corp. 2018, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class Visualization
 * @hideconstructor
 */
define([
	'underscore',
	'./VisualizationAPISpec',
	'../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../deprecated/VisualizationAPI',
	'../internal/VisualizationAPI',
	'../VisualizationAPI',
	'../cleanup/VisualizationAPI',
	'./VisOlapContentHelper',
	'../VisOlapContentHelperAPI',
	'./LocalFilters',
	'./Slots',
	'./SavedPrompts',
	'./serviceability/ServiceabilityDataInfo',
	'../../widgets/livewidget/modelapis/modelmanagers/VisModelManager',
	'../../widgets/livewidget/visapi/support/VisFilterSupport',
	'../../widgets/livewidget/visapi/support/VisPropertySupport',
	'./model/DataModel',
	'../../widgets/livewidget/nls/StringResources'


], (_, VisualizationAPISpec, APIFactory, DeprecatedVisualizationAPI, InternalVisualizationAPI,
	VisualizationAPI, CleanUpVisualizationAPI, VisOlapContentHelper, VisOlapContentHelperAPI, LocalFilters, Slots, SavedPrompts, ServiceabilityDataInfo,
	VisModelManager, VisFilterSupport, VisPropertySupport, DataModel, StringResources) => {


	// Used to indicate which fields are supported in the "compare" api 
	// Each field can have an exclude list to exclude certain attributes from the comparison
	const widgetMatchingConfig = {
		visId:{
			getValue: visModel => {
				return visModel.visId;
			},
			exclude: []
		},
		localFilters: {
			getValue: visModel => {
				const filters = visModel.localFilters;
				return filters ? filters.toJSON() : {};
			},
			exclude: ['id', 'readOnly', 'isMissing']
		}
	};

	class Visualization extends VisualizationAPISpec {

		constructor(options){
			super();
			this.doNotRegisterDataSourceUsage = options.doNotRegisterDataSourceUsage;
			this.state = options.features['state.internal'];
			this.widgetModel = options.features['Models.internal'].getWidgetModel();
			this.dashboardAPI = options.features['Dashboard.API'];
			this.logger = options.features['Dashboard.Logger'];
			this.content = options.content;
			this.colors = options.features['Dashboard.Colors'];
			this.transaction = options.features['Dashboard.Transaction'];
			this.dataSources = options.features['Dashboard.DataSources'];
			this.boardModel = options.features['Dashboard.internal'].getBoardModel();
			this.visDefinitions = options.features['Dashboard.VisDefinitions'];
			this.serviceability = options.features['Serviceability'];
			this.internalVisDefinitions = options.features['Dashboard.VisDefinitions.internal'];
			this.notification = options.features['Dashboard.Notification'];
			this.dashboardSettings = options.features['Dashboard.DashboardSettings'];
			this._isPreview = options.spec && options.spec.isPreview;
			this._isPredictPreview = options.spec && options.spec.isPredictPreview;

			// TODO - should be its own dashboard feature
			this.userProfile = this.dashboardAPI.getGlassCoreSvc('.UserProfile');

			if (!this.widgetModel.data) {
				this.widgetModel.data = {};
			}
			this.dataModel = new DataModel(this.widgetModel.data);

			this.visDefinitions.on('refresh', this.refreshSlotsAfterChanges, this);
		}


		refreshSlotsAfterChanges() {
			if(this.slotsImpl){
				const isEmpty = this.internalVisDefinitions.getRawDefinition(this.getDefinition().getId()).empty;
				if (!this.getDefinition().getState().getError() && !isEmpty) {
					this.slotsImpl.setDefinition(this.getDefinition());
					this.slotsImpl._initializeDataItems();
				}
			}
		}


		destroy() {
			if (this.destroyed){
				// TODO livewidget_cleanup -- remove the viz from the live widget feature
				// so that we don't need this..

				// Because we temporary register this as a lvie widget feature and content feature,
				// the destroy will be called twice.. don't bother the second time
				// This code can go away once one move to the content feature
				return;
			}

			const dataSource = this.getDataSource();
			if (dataSource && !this.doNotRegisterDataSourceUsage) {
				dataSource.deregisterUsage(this.widgetModel.id);
			}
			this.visDefinitions.off('refresh:definition', this.refreshSlotsAfterChanges, this);
			this.widgetModel = null;
			this.dashboardAPI =  null;
			this.logger = null;
			this.widget = null;
			this.content = null;
			this.colors = null;
			this.transaction = null;
			this.dataSources = null;
			this.boardModel = null;
			this.visDefinitions = null;
			this.internalVisDefinitions = null;
			this.userProfile = null;

			this.localFiltersImpl.destroy();
			this.localFiltersImpl = null;

			this.savedPromptsImpl.destroy();
			this.savedPromptsImpl = null;

			this.slotsImpl.destroy();
			this.slotsImpl = null;

			this.visOlapHelperImpl = null;
			this.visOlapHelper = null;

			this.destroyed = true;

		}

		_createLegacyManagers() {
			const visModelManager = new VisModelManager({
				logger: this.logger,
				localFilters: this.widgetModel.localFilters,
				searchFilters: this.widgetModel.searchFilters,
				visDefinitions: this.internalVisDefinitions,
				widgetModel: this.widgetModel,
				dashboardApi: this.dashboardAPI,
				boardModel: this.boardModel,
				colors: this.colors,
				content: this.content,
				visualizationFeature: this.getAPI()
			});

			var pageContextModel = this.boardModel.get('pageContext');
			var pageContext = pageContextModel && pageContextModel.getAPI();

			const visFilterSupport = new  VisFilterSupport({
				visModelManager: visModelManager,
				pageContext: pageContext,
				logger: this.logger,
				dashboardApi: this.dashboardAPI,
				content: this.content,
				contentId: this.widgetModel.id,
				visualization: this.getAPI()
			});

			const visPropertySupport = new VisPropertySupport({
				properties: this.widgetModel.properties,
				visModelManager: visModelManager,
				dashboardApi: this.dashboardAPI,
				content: this.content
			});

			visModelManager.initialize({
				propertySupport: visPropertySupport,
				filterSupport: visFilterSupport//,
			});

			this.legacyManagers = {
				visModelManager: visModelManager,
				visFilterSupport: visFilterSupport,
				visPropertySupport: visPropertySupport,
				dataModel: this.dataModel  // TODO livewidget_cleanup -- remove this.. it is here temporary for the visCOnditionSupport
			};
		}

		getLegacyManagers() {
			return this.legacyManagers;
		}

		getAPI(type) {
			if (type === 'legacy'){
				return {
					getLegacyManagers: this.getLegacyManagers.bind(this)
				};
			}
			if (type === 'internal') {
				if (!this.internal) {
					this.internal = APIFactory.createAPIFromSpec([{
						implementation: this,
						interface: VisualizationAPI
					}, {
						implementation: this,
						interface: InternalVisualizationAPI
					}, {
						implementation: this,
						interface: DeprecatedVisualizationAPI,
						options:{
							// Set true to enable deprecated logging
							isDeprecated: false
						}
					}]);
				}
				return this.internal;
			}
			if (type === 'cleanup') {
				if (!this.cleanup) {
					this.cleanup =  APIFactory.createAPIFromSpec([{
						implementation: this,
						interface: CleanUpVisualizationAPI
					}]);
				}
				return this.cleanup;
			}
			if (type === 'olapContentHelper') {
				if (!this.olapContentHelper) {
					this.olapContentHelperImpl = new VisOlapContentHelper(this);
					this.olapContentHelper =  APIFactory.createAPIFromSpec([{
						implementation: this.olapContentHelperImpl,
						interface: VisOlapContentHelperAPI
					}]);
				}
				return this.olapContentHelper;
			}
			if(!this._apiInstance){
				this._apiInstance = APIFactory.createAPIFromSpec([{
					implementation: this,
					interface: VisualizationAPI
				},{
					implementation: this,
					interface: DeprecatedVisualizationAPI,
					options:{
						// Set true to enable deprecated logging
						isDeprecated: false
					}
				}]);
			}
			return this._apiInstance;
		}

		/**
		 * @param {object} options
		 * @description one of the task this method will do is to set data mapping
		 * infomation to its associated contentInfo object. We put a try...catch...
		 * here because of we don't want to block creating a widget on a place which
		 * is not a canvas, like a conversation panel.
		 */
		initialize () {
			return this._ensureVisDefinition()
				.then(() => {
					const dataSource = this.getDataSource();
					let whenMetadataLoaded;
					if (dataSource) {
						if (!this.doNotRegisterDataSourceUsage){
							dataSource.registerUsage(this.widgetModel.id);
						}
						if(this.dashboardSettings.get('metadataBlocksVisualization')){
							whenMetadataLoaded = dataSource.loadMetadata().catch((e) => {
								// Swallow this loadMetadata failure since the MetadataTaskProvider
								// will handle the error on render already and failing to load metadata
								// should not prevent the Visualization feature creation. AppFactory users
								// may not be rendering the visualization though, so still show error traces.
								this.logger.log('An error occured while loading the metadata for content with id ' + this.content.getId());
								this.logger.log(e);
							});
						}
					}
					this._createLegacyManagers();
					this._createSubAPIs();

					// We don't support two cases where the following error will be thrown:
					// 1. live widgets on the conversation panel and,
					// 2. live widgets on the starting point page of an Exploration
					if (this.serviceability){
						this._initializeServiceability(this.serviceability);
					}
					this._initializePropsAndThemes();

					return whenMetadataLoaded;
				});
		}

		validateLocalFilters() {
			this._getFilterSupport().validateFilters();
		}

		_initializePropsAndThemes() {
			if (this.getType()){
				const definition = this.getDefinition();
				const propertySupport = this._getPropertySupport();
				propertySupport.buildProperties(definition.getProperty('properties') || definition.getProperty('propertyList'), this.boardModel.getLanguageModelOptions());
			}
		}

		_initializeServiceability(serviceability) {
			const s12yInfo = serviceability.getContentInfo();
			s12yInfo.setData('dataInfo', new ServiceabilityDataInfo(this));

			const generalInfo = s12yInfo.getData('generalInfo');
			generalInfo.getLabel = () => this.getDefinition().getLabel();
			s12yInfo.setData('generalInfo', generalInfo);
		}

		selectData(selection, options) {
			return this._getFilterSupport().select(selection, options);
		}

		_createSubAPIs(){
			this.localFiltersImpl =  new LocalFilters({ filterSupport: this._getFilterSupport(), visualization: this.getAPI('internal'), dataModel: this.dataModel, transaction: this.transaction });
			APIFactory.setParentChildRelation(this, this.localFiltersImpl);
			this.savedPromptsImpl = new SavedPrompts(this.widgetModel);
			APIFactory.setParentChildRelation(this, this.savedPromptsImpl);

			this.slotsImpl = new Slots(
				this.widgetModel,
				this.dataModel,
				this,
				this.transaction,
				this.userProfile.preferences.contentLocale,
				this.legacyManagers.visFilterSupport);

			APIFactory.setParentChildRelation(this, this.slotsImpl);
		}

		matches(spec){
			return this._matches(spec);
		}

		/**
		* Return true if the widget match the content provided in the parameter
		*	We currently only support match visId and localFilter. Anything else will cause an error.
		*
		* @param {Object} widgetContent - content to match.
		*/
		_matches(widgetContent) {
			let widgetContentAsString = '';
			let currentAsString = '';
			Object.keys(widgetContent).forEach((key) => {
				const matchingConfig = widgetMatchingConfig[key];
				if ( !matchingConfig ){
					// Not supported
					throw 'the matches API does not support matching the property "' + key + '"';
				}
				widgetContentAsString += this._getCompareString(widgetContent[key], matchingConfig.exclude);
				const currentValueToCompare = matchingConfig.getValue(this.widgetModel);
				currentAsString += this._getCompareString(currentValueToCompare, matchingConfig.exclude);
			});

			return widgetContentAsString === currentAsString;
		}

		/**
		* Build a unique string that represents a given object.
		* This string can be used to compare with another object
		* Certain props can be excluded from the string like ids.
		*
		*/
		_getCompareString(object, excludeAttributes) {
			let compareString = '';
			if (typeof object === 'object') {
				const compareValues = [];
				if (Array.isArray(object)) {
					object.forEach((value) => {
						compareValues.push(this._getCompareString(value, excludeAttributes));
					});
				} else {
					let name;
					for (name in object) {
						if (excludeAttributes.indexOf(name) === -1) {
							compareValues.push(name + '_' + this._getCompareString(object[name], excludeAttributes));
						}
					}
				}
				compareValues.sort();
				compareValues.forEach((value) => {
					compareString += value;
				});
			} else {
				compareString += object;
			}
			return compareString;
		}

		getType() {
			let type = null;
			const definition = this.getDefinition();
			if(definition){
				type = definition.getType();
			}
			return type;
		}

		setType(type) {
			if (type && type !== this.getType()){
				const newDefinition = this.visDefinitions.getByType(type);
				if (newDefinition) {
					this.widgetModel.visId = newDefinition.getId();
					this.slotsImpl.setDefinition(newDefinition);

					this._initializePropsAndThemes();
					this.refreshSlotsAfterChanges();
				} else {
					throw new Error('Invalid type');
				}
			}
		}

		isTypeLocked () {
			var isLocked = this.widgetModel.get('visTypeLocked');
			return !!isLocked;
		}

		lockType() {
			this.widgetModel.visTypeLocked = true;
		}

		unlockType() {
			this.widgetModel.visTypeLocked = false;
		}

		getDefinition() {
			const definition = this.visDefinitions.getById(this.widgetModel.visId);
			return definition;
		}

		getDataSource() {
			// PA Support: In PA, the dataSource is bound to the content.  This means that the dataSourceId and content Id are identical
			// This situation does not currently occur in CA where we use the modelRef of the view to determine the dataSourceId.
			let dataSource = this.content && this.dataSources.getDataSource(this.content.getId());
			if (!dataSource) {
				const dataView = this.dataModel.getDataView();
				dataSource = this.dataSources.getDataSource(dataView && dataView.modelRef);
			}
			return dataSource || null;
		}

		setDataSource(sourceId) {
			const currentDataSource = this.getDataSource();
			const currentId = currentDataSource && currentDataSource.getId();
			const visDef = this.getDefinition();
			if(currentId !== sourceId){
				if (currentId !== null && (this.slotsImpl.getMappedSlotList().length > 0 || this.getLocalFilters().getFilterList().length > 0)){
					throw ('Cannot change dataSource while the visualiation contains dataItems or filters from another source');
				}
				const dataSource = this.dataSources.getDataSource(sourceId);
				if (dataSource) {
					if (dataSource.isOlapPackage() && visDef && visDef.getProperty('supportsOLAP') === false) {
						// todo livewidget_cleanup - add notification to dependencies in perspective file in the server
						this.dashboardAPI.getFeature('Notification').setMessage(StringResources.get('visualizationDoesNotSupportOLAP'));
						return false;
					}
					if (!this.doNotRegisterDataSourceUsage){
						dataSource.registerUsage(this.widgetModel.id);
					}
				}
				if (currentDataSource && !this.doNotRegisterDataSourceUsage) {
					currentDataSource.deregisterUsage(this.widgetModel.id);
				}
				this.dataModel.setModelRefInAllViews(sourceId);
				return true;
			}
			return false;
		}

		/**
		 * @description Get an array of MetadataColumnAPI objects which are being used in the visualization
		 * @return {MetadataColumnAPI[]}
		 */
		getUsedMetadataColumnList() {
		}

		getSlots() {
			return this.slotsImpl.getAPI();
		}

		getSavedPrompts() {
			return this.savedPromptsImpl.getAPI();
		}


		getLocalFilters() {
			return this.localFiltersImpl.getAPI();
		}

		getSearchFilterList() {
			return this._getVisModelManager().searchFilters.toJSON();
		}


		// /**
		//  * @description Returns the decorator object that can be used to decorate the data in the visualization.
		//  * If the visualizatoin support multiple datasets and no view id is provided, then the decorator for the first dataset is returned
		//  *
		//  * @param {String} [viewId]  Data view id. If not provided, the decorator for the first data view is returned.
		//  *
		//  * @return {DecoratorAPI} decorator object
		//  *
		//  */
		// getDecorator(/* dataViewId */) { }


		// /**
		//  * @description Return a list of decorator object that can be used to decorate the data in the visualization.
		//  * Each dataset will have its own decorator object.
		//  *
		//  *
		//  * @return {DecoratorAPI[]} decorator object
		//  *
		//  */
		// getDecoratorList() {

		// 	if (!this.decorators) {
		// 		this.decorators = [];
		// 		const oldAPIs = this.widget.getDecoratorAPIs();
		// 		if (oldAPIs) {
		// 			oldAPIs.forEach(oldAPI => {
		// 				this.decorators.push(new Decorator(oldAPI).getAPI());
		// 			});
		// 		}
		// 	}
		// 	return this.decorators;

		// }

		/**
		 * @description Returns true if the visualization references a column that is no longer available
		 *
		 * @returns {Boolean}
		 */
		hasUnavailableMetadataColumns() {
			const slotWithUnavailableColumns = this.getSlots().getSlotList().find(slot => {
				return slot.hasUnavailableMetadataColumns();
			});
			return !!slotWithUnavailableColumns;
		}

		_convertTransactionTokenToLegacyOptions(transactionToken){
			const oldOptions = {};
			if(transactionToken){
				oldOptions.payloadData = {
					transactionToken: transactionToken
				};
				if(transactionToken.transactionId){
					oldOptions.payloadData.undoRedoTransactionId = transactionToken.transactionId;
				}
			}

			return oldOptions;
		}

		isPreview() {
			return !!this._isPreview;
		}

		isPredictPreview() {
			return !!this._isPredictPreview;
		}

		_getFilterSupport() {
			return this.legacyManagers.visFilterSupport;
		}

		_getVisModelManager() {
			return this.legacyManagers.visModelManager;
		}

		_getPropertySupport(){
			return this.legacyManagers.visPropertySupport;
		}

		refreshSlotsCache() {
			this.slotsImpl.invalidateCache();
		}

		_ensureVisDefinition(){
			// On initialization of this feature this.widgetModel.visId is undefined if the
			// recommender is initializing the Visualization feature. The recommeder 
			// recommends only system viz-es which will always have their definitions loaded.
			// No need to refresh visDefinition from undefined visId. This will lead to an error.
			if (this.widgetModel.visId) {
				const visDefinition = this.getDefinition();
				if (visDefinition.getState().getError()) {
					// refresh for a deleted custom viz will throw an error, but the feature is still valid, so we trap it
					return visDefinition.refresh().then(() => true).catch(() => true);
				}
			}
			return Promise.resolve(true);
		}

		setExtendedRenderWidth(width) {
			this._extendedRenderWidth = width;
		}

		getExtendedRenderWidth() {
			return this._extendedRenderWidth;
		}
	}

	return Visualization;
});
